import { useState, useCallback } from "react";
import Cookies from "js-cookie";
import { getApi } from "../utils/api.ts";
import { TodayPunchData } from "../components/features/dashboard/components/attendance-card/types.ts";
import { API_GET_PUNCH_DETAILS_BY_DATE } from "../../config/Endpoints.js";

type PunchApiEnvelope = {
  statusCode?: number;
  message?: string;
  data?: TodayPunchData[] | TodayPunchData | null;
};

/** `data` may be an array (week) or a single day object. */
function extractPunchRows(envelope: unknown): TodayPunchData[] {
  if (envelope == null || typeof envelope !== "object") return [];
  const inner = (envelope as PunchApiEnvelope).data;
  if (Array.isArray(inner)) return inner;
  if (inner != null && typeof inner === "object") return [inner as TodayPunchData];
  return [];
}

function normalizeRow(row: TodayPunchData): TodayPunchData {
  const raw = row.activeWorkingHours as unknown;
  const n =
    typeof raw === "string"
      ? Number(raw)
      : typeof raw === "number"
        ? raw
        : NaN;
  return {
    ...row,
    activeWorkingHours: Number.isFinite(n) ? n : row.activeWorkingHours,
  };
}

interface UseTodayPunchReturn {
  weekData: TodayPunchData[];
  loading: boolean;
  error: string | null;
  fetch: () => Promise<void>;
  clear: () => void;
}

/** Single punch-details request (no `?date=`); chart bar selection reads from `weekData` only. */
export const useTodayPunch = (): UseTodayPunchReturn => {
  const [weekData, setWeekData] = useState<TodayPunchData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = Cookies.get("accessToken");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getApi<PunchApiEnvelope>(
        API_GET_PUNCH_DETAILS_BY_DATE,
        token,
      );
      const rows = extractPunchRows(res.data).map(normalizeRow);
      setWeekData(rows);
      setError(null);
    } catch {
      setError("Failed to fetch data");
      setWeekData([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const clear = useCallback(() => {
    setError(null);
  }, []);

  return {
    weekData,
    loading,
    error,
    fetch,
    clear,
  };
};
